import { useInfiniteQuery } from '@tanstack/react-query';
import type React from 'react';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useRecoilValue } from 'recoil';
import api from '@/services/apiSgft';
import { employeeByActivityPoleFilterAtom } from '@/state/EmployeeByActivityPole.atom';
import type { EmployeeByActivityPole } from '@/types/EmployeeByActivityPole';
import { getQueryParams } from '@/utils/EmployeeByActivityPole/get-employee-by-activity-pole-query-params.utils';
import Spinner from '../atoms/Spinner';
import { EmployeeByActivityPoleCard } from './EmployeeByActivityPoleCard';
import EmployeeSearchCard from './EmployeeSearchCard';

interface EmployeeByActivityPoleTabProps {
	allowed: boolean;
	atRiskOfBlocking?: boolean;
	employeeSearch: string;
	debouncedEmployeeSearch: string;
	handleUpdateEmployeeSearch: (search: string) => void;
}

export const EmployeeByActivityPoleTab: React.FC<
	EmployeeByActivityPoleTabProps
> = ({
	allowed,
	atRiskOfBlocking,
	employeeSearch,
	debouncedEmployeeSearch,
	handleUpdateEmployeeSearch,
}) => {
	const { ref, inView } = useInView();
	const filteredData = useRecoilValue(employeeByActivityPoleFilterAtom);

	const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery<
		EmployeeByActivityPole[]
	>({
		queryFn: async ({ pageParam = 1 }) => {
			const queryParams = getQueryParams(filteredData);
			const response = await api.get('activity/employee-by-activity-pole/', {
				params: {
					page: pageParam,
					limit: 20,
					allowed,
					atRiskOfBlocking,
					employeeSearch: debouncedEmployeeSearch,
					...queryParams,
				},
			});
			return response.data;
		},
		queryKey: [
			'employee-by-activity-pole',
			filteredData,
			debouncedEmployeeSearch,
			allowed,
			atRiskOfBlocking,
		],
		getNextPageParam: (lastPage, allPages) => {
			// Retorna o próximo número de página, se houver mais dados
			return lastPage.length > 0 ? allPages.length + 1 : undefined;
		},
	});

	// Detecta o scroll no final da lista
	useEffect(() => {
		if (inView && hasNextPage) {
			fetchNextPage();
		}
	}, [fetchNextPage, inView, hasNextPage]);

	return (
		<div className="flex h-full max-w-full flex-col gap-4 ">
			<EmployeeSearchCard
				searchInput={employeeSearch}
				handleInputChange={handleUpdateEmployeeSearch}
			/>
			{isLoading && (
				<div className="flex h-80 w-full items-center justify-center rounded-md bg-white">
					<Spinner size={40} />
				</div>
			)}
			<div className="custom-scrollbar flex max-w-full flex-col gap-3 px-1">
				{data?.pages?.map((page, pageIndex) =>
					page.map((employeeByActivityPole) => (
						<EmployeeByActivityPoleCard
							employeeByActivityPole={employeeByActivityPole}
							allowed={allowed}
							atRiskOfBlocking={atRiskOfBlocking}
							key={`${employeeByActivityPole.id}-${pageIndex}`}
						/>
					)),
				)}
				{hasNextPage && (
					<div className="flex w-full items-center justify-center" ref={ref}>
						<Spinner size={30} />
					</div>
				)}
			</div>
		</div>
	);
};
